// Camada fina sobre Firestore. Centraliza:
//  - referências canônicas (trips/{id}, sub-coleções, meta docs)
//  - CRUD com stamping automático de userId, createdAt, updatedAt
//  - enforcement de `userId == auth.uid` já ocorre via Security Rules;
//    aqui só garantimos que os writes incluem o campo correto.
//
// Shape (ver firestore.rules e docs/FIREBASE_MIGRATION_PLAN.md):
//   trips/{tripId}                       → metadados da viagem + userId
//   trips/{tripId}/timeline/{dayId}      → 1 doc por dia
//   trips/{tripId}/hotels/{hotelId}
//   trips/{tripId}/routes/{routeId}
//   trips/{tripId}/expenses/{expId}
//   trips/{tripId}/itinerary/{itemId}
//   trips/{tripId}/futureReceivables/{id}
//   trips/{tripId}/documents/{docId}
//   trips/{tripId}/meta/{balance|currentBalance|currency}

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';

import { auth, db } from './firebase.js';
import { waitForAuth } from './auth.js';
import { commitWrite } from './offlineWrite.js';

// ---------- guards ----------

/**
 * Retorna o uid do usuário logado.
 *
 * `auth.currentUser` é populado de forma assíncrona por Firebase após ler
 * o estado persistido (IndexedDB). Logo após o boot da página, mesmo em
 * contextos que já aguardaram `ensureAuthenticated()` numa tag <script>
 * vizinha, pode existir um tick em que `auth.currentUser === null` em
 * outros módulos — especialmente com classic-defer + module-defer misturados.
 *
 * Fallback seguro: se currentUser ainda não está lá, aguarda o primeiro
 * `onAuthStateChanged` via `waitForAuth()`. O custo é microtask-ish na
 * primeira chamada; chamadas subsequentes leem síncronas.
 */
async function requireUid() {
  const user = auth.currentUser || await waitForAuth();
  if (!user) throw new Error('Operação requer login — sem currentUser.');
  return user.uid;
}

// ---------- trips ----------

/** Lista todas as viagens que o usuário logado tem acesso (admin ou member). */
export async function listMyTrips() {
  const uid = await requireUid();
  // Query com índice composto (memberIds array-contains, startDate DESC) — ver firestore.indexes.json
  const q = query(
    collection(db, 'trips'),
    where('memberIds', 'array-contains', uid),
    orderBy('startDate', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Busca uma viagem por id. Retorna null se não existir. Rules garantem que só o dono lê. */
export async function getTrip(tripId) {
  const snap = await getDoc(doc(db, 'trips', tripId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Cria/substitui uma viagem. Força userId = auth.uid e carimba createdAt/updatedAt.
 * Se `data.id` existir, usa; senão gera um id alfanumérico do Firestore.
 */
export async function upsertTrip(data) {
  const uid = await requireUid();
  const isNew = !data.id;
  const id = data.id || doc(collection(db, 'trips')).id;

  // Pra trips NOVAS (sem id), pulamos o getDoc — não precisa preservar nada e
  // tira uma leitura cross-document que pode retornar permission-denied caso
  // a rule de read não permita doc inexistente.
  // Pra updates (id passado), lemos o doc existente pra preservar
  // createdAt/memberIds/members (rules bloqueiam alterar userId/members).
  const existingData = isNew ? null : await (async () => {
    const snap = await getDoc(doc(db, 'trips', id));
    return snap.exists() ? snap.data() : null;
  })();

  const createdAt = existingData?.createdAt ?? serverTimestamp();
  const memberIds = existingData?.memberIds ?? [uid];     // novo trip: criador é único membro
  const members   = existingData?.members   ?? {
    [uid]: {
      email: auth.currentUser?.email || null,
      role: 'admin',
      // Date → Firestore Timestamp automaticamente; serverTimestamp() não
      // funciona dentro de map nested em setDoc sem merge.
      addedAt: new Date(),
    },
  };

  const payload = {
    ...data,
    id,
    userId: existingData?.userId ?? uid,   // admin nunca muda via upsert
    memberIds,
    members,
    createdAt,
    updatedAt: serverTimestamp(),
  };
  await commitWrite(setDoc(doc(db, 'trips', id), payload));
  return { ...payload, id };
}

export async function updateTrip(tripId, patch) {
  // userId não pode mudar — rules bloqueiam, mas removemos do patch por segurança.
  const { userId, id, createdAt, ...clean } = patch;
  await commitWrite(updateDoc(doc(db, 'trips', tripId), {
    ...clean,
    updatedAt: serverTimestamp(),
  }));
}

/**
 * Deleta a viagem + todos os docs das sub-coleções conhecidas.
 * Firestore não cascateia sozinho.
 */
export async function deleteTripWithChildren(tripId) {
  // Mantenha em sincronia com TRIP_SUBCOLLECTIONS em
  // functions/_lib/services/accountDeletionService.js — as duas listas
  // apagam a mesma árvore (aqui pelo cliente, lá pelo Service Account).
  const SUBS = [
    'timeline', 'itinerary', 'hotels', 'routes',
    'expenses', 'futureReceivables',
    'documents', 'documents_v2', 'checklist', 'shopping',
    'meta', 'settings', 'roteiroVersions', 'aiDayJobs',
  ];

  for (const sub of SUBS) {
    const snap = await getDocs(collection(db, 'trips', tripId, sub));
    // Batch por 500 — limite do Firestore
    let batch = writeBatch(db);
    let count = 0;
    for (const d of snap.docs) {
      batch.delete(d.ref);
      count++;
      if (count === 500) {
        await batch.commit();
        batch = writeBatch(db);
        count = 0;
      }
    }
    if (count > 0) await batch.commit();
  }

  await deleteDoc(doc(db, 'trips', tripId));
}

// ---------- sub-coleções genéricas ----------

export async function listSubCollection(tripId, name) {
  const snap = await getDocs(collection(db, 'trips', tripId, name));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getSubDoc(tripId, name, docId) {
  const snap = await getDoc(doc(db, 'trips', tripId, name, docId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Upsert. Carimba updatedAt.
 * `merge: true` (default) preserva campos não enviados no payload (bom pra
 * patches incrementais). `merge: false` substitui o doc inteiro (necessário
 * para blobs `_legacy` onde o payload representa o estado completo).
 */
export async function setSubDoc(tripId, name, docId, data, { merge = true } = {}) {
  const ref = doc(db, 'trips', tripId, name, docId);
  await commitWrite(setDoc(ref, { ...data, updatedAt: serverTimestamp() }, { merge }));
}

export async function deleteSubDoc(tripId, name, docId) {
  await commitWrite(deleteDoc(doc(db, 'trips', tripId, name, docId)));
}

/**
 * Substitui toda uma sub-coleção pelos items fornecidos em um único batch.
 * Usado quando o shape antigo era um blob único e a UI edita o array inteiro.
 * Items sem `id` recebem um gerado.
 */
export async function replaceSubCollection(tripId, name, items) {
  const col = collection(db, 'trips', tripId, name);

  // Apaga tudo primeiro
  const existing = await getDocs(col);
  let batch = writeBatch(db);
  let count = 0;
  const flush = async () => { await batch.commit(); batch = writeBatch(db); count = 0; };

  for (const d of existing.docs) {
    batch.delete(d.ref);
    if (++count === 500) await flush();
  }
  // Insere os novos
  for (const item of items || []) {
    const id = item.id || doc(col).id;
    batch.set(doc(col, id), { ...item, id, updatedAt: serverTimestamp() });
    if (++count === 500) await flush();
  }
  if (count > 0) await batch.commit();
}

// ---------- meta docs (balance, currency, currentBalance) ----------

export async function getMeta(tripId, name) {
  const snap = await getDoc(doc(db, 'trips', tripId, 'meta', name));
  return snap.exists() ? snap.data() : null;
}

export async function setMeta(tripId, name, data, { merge = true } = {}) {
  const ref = doc(db, 'trips', tripId, 'meta', name);
  await commitWrite(setDoc(ref, { ...data, updatedAt: serverTimestamp() }, { merge }));
}
